import { useCallback, useEffect, useRef, useState } from 'react';
import { authService, useAuthStore } from '@/entities/auth';
import { readSessionExpiresAt } from '../lib/sessionExpiresAt';

/** За сколько до конца сессии пользователю предлагается её продлить */
const OFFER_THRESHOLD_MS = 60_000;

/** Шаг отсчёта внутри предложения: с ним обновляется показанное оставшееся время */
const TICK_MS = 1_000;

/**
 * Отсчёт до конца сессии и её продление по решению пользователя.
 *
 * Возвращает признак показа предложения, оставшиеся до конца сессии секунды,
 * признак ожидания ответа на продление и само действие продления.
 */
export const useSessionExpiry = () => {
  const expireSession = useAuthStore((state) => state.expireSession);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [isExtending, setIsExtending] = useState(false);
  // Последний известный момент истечения: признак сессии снимается вместе с её
  // концом, и без запомненного момента отсчёт оборвался бы перед самым нулём
  const knownExpiresAtRef = useRef<number | null>(null);
  // Конец отсчёта наступает один раз: следующие шаги отсчёта завершение не повторяют
  const isSessionEndedRef = useRef(false);
  // Внеочередной шаг отсчёта: продление меняет момент истечения раньше,
  // чем наступит запланированный шаг
  const resyncRef = useRef<() => void>(() => {
    // До первого шага отсчёта пересинхронизировать нечего
  });

  // Конец отсчёта завершает сессию: работа прекращается, экран входа сообщает о её потере.
  // Сессия закрывается и на стороне системы — по часам системы срок мог ещё не выйти
  // (часы рабочего места идут иначе), и без явного закрытия проверка сессии вернула бы
  // пользователя в работу вопреки только что показанному сообщению
  const endSession = useCallback(() => {
    if (isSessionEndedRef.current) {
      return;
    }

    isSessionEndedRef.current = true;
    expireSession();
    void authService.logout();
  }, [expireSession]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Шаг отсчёта: момент истечения перечитывается каждый раз — обращения пользователя
    // продлевают сессию сами, поэтому отсчёт идёт до текущего конца сессии,
    // а не до сообщённого при входе
    const tick = () => {
      const expiresAt = readSessionExpiresAt() ?? knownExpiresAtRef.current;

      if (expiresAt === null) {
        setRemainingMs(null);
        // Момент истечения ещё не сообщён: редкая проверка вместо остановки отсчёта
        timer = setTimeout(tick, OFFER_THRESHOLD_MS);
        return;
      }

      knownExpiresAtRef.current = expiresAt;

      const remaining = expiresAt - Date.now();
      setRemainingMs(remaining);

      if (remaining <= 0) {
        endSession();
        return;
      }

      // До окна предложения оставшееся время на экране не нужно: хватает одного шага
      // к его началу. Ежесекундный отсчёт идёт только внутри окна, где время показано
      timer = setTimeout(tick, remaining > OFFER_THRESHOLD_MS ? remaining - OFFER_THRESHOLD_MS : TICK_MS);
    };

    resyncRef.current = () => {
      if (timer !== null) {
        clearTimeout(timer);
      }

      tick();
    };

    tick();

    return () => {
      if (timer !== null) {
        clearTimeout(timer);
      }

      resyncRef.current = () => {
        // Отсчёт снят: внеочередной шаг сдвигать уже нечему
      };
    };
  }, [endSession]);

  const extend = useCallback(async () => {
    setIsExtending(true);

    try {
      await authService.extendSession();
      // Новый момент истечения приходит с продлённой сессией — снимаем
      // предложение сразу, не дожидаясь следующего шага отсчёта
      resyncRef.current();
    } catch {
      // Отказ в продлении оставляет отсчёт идти дальше: конец сессии
      // обработается общим порядком
    } finally {
      setIsExtending(false);
    }
  }, []);

  return {
    isOfferShown: remainingMs !== null && remainingMs > 0 && remainingMs <= OFFER_THRESHOLD_MS,
    remainingSeconds: remainingMs === null ? 0 : Math.max(0, Math.ceil(remainingMs / 1000)),
    isExtending,
    extend,
  };
};
